import { useEffect, useMemo, useRef, useState } from 'react';
import { elementColor } from '../elements';
import {
  cueHighlights,
  cueIsActive,
  DARK_INK,
  NowYouTry,
  useMountSequence,
  usePrefersReducedMotion,
  VizCard,
  VIZ_BTN_GHOST,
  VIZ_FOCUS,
  type VizCue,
} from '../vizShared';

// LewisStructure — render the Lewis (electron-dot) structure of a small molecule
// from config.formula. Draws the skeleton (central atom + terminal atoms), bond
// pairs as lines (single/double/triple), lone pairs as dot pairs, and formal
// charges. Interactive toggles show/hide lone pairs and a per-atom electron
// tally. Backed by a small recipe table of common octet molecules; resilient
// default to H2O.
//
// Self-demo: on first mount (unless reduced-motion or a cue is driving) the
// structure builds up in stages — skeleton → bonds → lone pairs → counts — then
// settles and shows a "Now you try" pill. Any interaction cancels it.
//
// cue: { step? → placement stage 0..3; progress? → 0..1 reveal scrubber;
//        highlight? → spotlight an atom symbol/bond }
//
// config: { formula?, title?, showLonePairs?, showCounts? }

export interface LewisStructureConfig {
  title?: string;
  formula?: string;
  showLonePairs?: boolean;
  showCounts?: boolean;
}

interface Props {
  config?: LewisStructureConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
  // WIP-016 (design §8.3) — reports the selected molecule as a step's answer.
  // Optional and additive: nothing below calls this unless a caller (the
  // classroom's StepPrompt, when a step's answer is this widget) passes it,
  // so every other caller of LewisStructure — the lecture player, the
  // Virtual Lab gallery — is completely unaffected.
  onStateChange?: (snapshot: { state: unknown; display: string }) => void;
}

// A terminal atom hung off the central atom.
interface Terminal {
  symbol: string;
  bonds: 1 | 2 | 3; // bond order to the center
  lonePairs: number; // lone pairs on this terminal atom
  formal?: number; // formal charge
}

interface Recipe {
  name: string;
  central: string;
  centralLonePairs: number;
  centralFormal?: number;
  terminals: Terminal[];
  // Optional: render terminals around a straight axis (linear molecules)
  linear?: boolean;
  note?: string;
}

// Octet-satisfying Lewis recipes for the molecules students draw most. Each
// double/triple bond and lone-pair count is the textbook structure.
const RECIPES: Record<string, Recipe> = {
  H2O: {
    name: 'Water',
    central: 'O',
    centralLonePairs: 2,
    terminals: [
      { symbol: 'H', bonds: 1, lonePairs: 0 },
      { symbol: 'H', bonds: 1, lonePairs: 0 },
    ],
    note: 'Bent · 2 bonding + 2 lone pairs on O',
  },
  CO2: {
    name: 'Carbon dioxide',
    central: 'C',
    centralLonePairs: 0,
    linear: true,
    terminals: [
      { symbol: 'O', bonds: 2, lonePairs: 2 },
      { symbol: 'O', bonds: 2, lonePairs: 2 },
    ],
    note: 'Linear · two C=O double bonds',
  },
  NH3: {
    name: 'Ammonia',
    central: 'N',
    centralLonePairs: 1,
    terminals: [
      { symbol: 'H', bonds: 1, lonePairs: 0 },
      { symbol: 'H', bonds: 1, lonePairs: 0 },
      { symbol: 'H', bonds: 1, lonePairs: 0 },
    ],
    note: 'Trigonal pyramidal · 1 lone pair on N',
  },
  CH4: {
    name: 'Methane',
    central: 'C',
    centralLonePairs: 0,
    terminals: [
      { symbol: 'H', bonds: 1, lonePairs: 0 },
      { symbol: 'H', bonds: 1, lonePairs: 0 },
      { symbol: 'H', bonds: 1, lonePairs: 0 },
      { symbol: 'H', bonds: 1, lonePairs: 0 },
    ],
    note: 'Tetrahedral · no lone pairs on C',
  },
  HCN: {
    name: 'Hydrogen cyanide',
    central: 'C',
    centralLonePairs: 0,
    linear: true,
    terminals: [
      { symbol: 'H', bonds: 1, lonePairs: 0 },
      { symbol: 'N', bonds: 3, lonePairs: 1 },
    ],
    note: 'Linear · C≡N triple bond',
  },
  O2: {
    name: 'Oxygen',
    central: 'O',
    centralLonePairs: 2,
    linear: true,
    terminals: [{ symbol: 'O', bonds: 2, lonePairs: 2 }],
    note: 'O=O double bond',
  },
  N2: {
    name: 'Nitrogen',
    central: 'N',
    centralLonePairs: 1,
    linear: true,
    terminals: [{ symbol: 'N', bonds: 3, lonePairs: 1 }],
    note: 'N≡N triple bond',
  },
  HF: {
    name: 'Hydrogen fluoride',
    central: 'F',
    centralLonePairs: 3,
    linear: true,
    terminals: [{ symbol: 'H', bonds: 1, lonePairs: 0 }],
    note: 'Single bond · 3 lone pairs on F',
  },
  CO: {
    name: 'Carbon monoxide',
    central: 'C',
    centralLonePairs: 1,
    centralFormal: -1,
    linear: true,
    terminals: [{ symbol: 'O', bonds: 3, lonePairs: 1, formal: 1 }],
    note: 'C≡O triple bond · formal charges',
  },
  SO2: {
    name: 'Sulfur dioxide',
    central: 'S',
    centralLonePairs: 1,
    terminals: [
      { symbol: 'O', bonds: 2, lonePairs: 2 },
      { symbol: 'O', bonds: 1, lonePairs: 3 },
    ],
    note: 'Bent · resonance (one form shown)',
  },
  BF3: {
    name: 'Boron trifluoride',
    central: 'B',
    centralLonePairs: 0,
    terminals: [
      { symbol: 'F', bonds: 1, lonePairs: 3 },
      { symbol: 'F', bonds: 1, lonePairs: 3 },
      { symbol: 'F', bonds: 1, lonePairs: 3 },
    ],
    note: 'Trigonal planar · B is electron-deficient (sextet)',
  },
  CCl4: {
    name: 'Carbon tetrachloride',
    central: 'C',
    centralLonePairs: 0,
    terminals: [
      { symbol: 'Cl', bonds: 1, lonePairs: 3 },
      { symbol: 'Cl', bonds: 1, lonePairs: 3 },
      { symbol: 'Cl', bonds: 1, lonePairs: 3 },
      { symbol: 'Cl', bonds: 1, lonePairs: 3 },
    ],
    note: 'Tetrahedral',
  },
};

const RECIPE_KEYS = Object.keys(RECIPES);

const W = 360;
const H = 300;
const CX = W / 2;
const CY = H / 2;
const BOND_LEN = 86;
const ATOM_R = 18;

// Build stages for the self-demo: 0 = skeleton only, 1 = bonds, 2 = lone pairs,
// 3 = everything (counts revealed). cue.step maps onto this.
const MAX_STAGE = 3;

// Normalize a formula key for lookup (strip whitespace, case-fix common keys).
function lookup(formula?: string): { key: string; recipe: Recipe; supported: boolean } {
  const raw = (formula ?? '').replace(/\s+/g, '');
  if (raw && RECIPES[raw]) return { key: raw, recipe: RECIPES[raw], supported: true };
  // Try a case-insensitive match.
  const ci = RECIPE_KEYS.find((k) => k.toLowerCase() === raw.toLowerCase());
  if (ci) return { key: ci, recipe: RECIPES[ci], supported: true };
  // Unknown formula: DON'T silently masquerade as water. Caller renders a
  // formula-only fallback. (We still return H2O geometry as a harmless default
  // for any path that ignores `supported`.)
  return { key: 'H2O', recipe: RECIPES.H2O, supported: false };
}

function chargeText(c?: number): string | null {
  if (!c) return null;
  const mag = Math.abs(c) === 1 ? '' : String(Math.abs(c));
  return c > 0 ? `${mag}+` : `${mag}−`;
}

// Place terminal atoms around the center. Linear molecules go left/right; others
// spread evenly with the first slot pointing up.
function placements(recipe: Recipe): Array<{ x: number; y: number; angle: number }> {
  const n = recipe.terminals.length;
  const out: Array<{ x: number; y: number; angle: number }> = [];
  if (recipe.linear) {
    const angles = n === 1 ? [0] : [Math.PI, 0]; // left, right
    for (const a of angles) out.push({ x: CX + Math.cos(a) * BOND_LEN, y: CY + Math.sin(a) * BOND_LEN, angle: a });
    return out;
  }
  for (let i = 0; i < n; i++) {
    // Start at top (-90°) and go clockwise.
    const a = -Math.PI / 2 + (i / n) * Math.PI * 2;
    out.push({ x: CX + Math.cos(a) * BOND_LEN, y: CY + Math.sin(a) * BOND_LEN, angle: a });
  }
  return out;
}

// Render lone-pair dots arranged just outside an atom on the side(s) AWAY from
// its bonds. `occupied` is the set of angles (radians) pointing at bonds.
function lonePairDots(
  cx: number,
  cy: number,
  pairs: number,
  occupied: number[],
): Array<{ x1: number; y1: number; x2: number; y2: number }> {
  if (pairs <= 0) return [];
  const slots = [-Math.PI / 2, Math.PI / 2, Math.PI, 0]; // top, bottom, left, right
  // Prefer slots farthest from any bond.
  const ranked = slots
    .map((a) => ({
      a,
      score: Math.min(...occupied.map((o) => Math.abs(angleDiff(a, o))).concat([Math.PI])),
    }))
    .sort((p, q) => q.score - p.score)
    .map((p) => p.a);
  const r = ATOM_R + 7;
  const spread = 5; // half-gap between the two dots of a pair
  const out: Array<{ x1: number; y1: number; x2: number; y2: number }> = [];
  for (let i = 0; i < Math.min(pairs, ranked.length); i++) {
    const a = ranked[i];
    const px = cx + Math.cos(a) * r;
    const py = cy + Math.sin(a) * r;
    // Perpendicular offset so the two dots straddle the radial line.
    const perp = a + Math.PI / 2;
    out.push({
      x1: px + Math.cos(perp) * spread,
      y1: py + Math.sin(perp) * spread,
      x2: px - Math.cos(perp) * spread,
      y2: py - Math.sin(perp) * spread,
    });
  }
  return out;
}

function angleDiff(a: number, b: number): number {
  let d = a - b;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return d;
}

export default function LewisStructure({ config = {}, onComplete, cue, onStateChange }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);

  const [key, setKey] = useState(() => lookup(config.formula).key);
  const [showLone, setShowLone] = useState(config.showLonePairs ?? true);
  const [showCounts, setShowCounts] = useState(config.showCounts ?? false);
  // Build stage drives the staged reveal (0..MAX_STAGE). Settle = fully built.
  const [stage, setStage] = useState<number>(reduced || hasCue ? MAX_STAGE : 0);
  const interactedRef = useRef(false);

  const recipe = RECIPES[key] ?? RECIPES.H2O;

  // One-shot build-up self-demo. Skipped under reduced motion or an active cue.
  const demo = useMountSequence(
    [
      { at: 250, run: () => setStage(1) },
      { at: 800, run: () => setStage(2) },
      { at: 1450, run: () => setStage(3) },
    ],
    {
      skip: reduced || hasCue,
      onSettle: () => setStage(MAX_STAGE),
    },
  );

  const markInteracted = () => {
    demo.cancel();
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.({ type: 'lewis_structure', formula: key });
    }
  };

  // ----- Cue handling (supersedes the self-demo) -----
  // cue.step → jump to a build stage.
  useEffect(() => {
    if (cue?.step == null) return;
    setStage(Math.max(0, Math.min(MAX_STAGE, Math.round(cue.step))));
  }, [cue?.step]);
  // cue.progress (0..1) → scrub the reveal across stages.
  useEffect(() => {
    if (cue?.progress == null) return;
    const p = Math.max(0, Math.min(1, cue.progress));
    setStage(Math.round(p * MAX_STAGE));
  }, [cue?.progress]);

  // WIP-016 — report the built structure as a step's answer (design §8.3).
  // The molecule choice IS the structure; lone-pair/electron-count toggles
  // are display options on the same structure, not a different answer, so
  // they are deliberately not in this effect's dependencies.
  useEffect(() => {
    onStateChange?.({ state: { formula: key }, display: `${key} (${recipe.name})` });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, recipe.name, onStateChange]);

  // Spotlight set from cue.highlight (atom symbols, lowercased).
  const highlights = useMemo(() => cueHighlights(cue), [cue]);
  const isLit = (sym: string) =>
    highlights.length > 0 && highlights.includes(sym.toLowerCase());
  const anyLit = highlights.length > 0;

  const geom = useMemo(() => placements(recipe), [recipe]);

  // Total valence-electron bookkeeping for the readout.
  const tally = useMemo(() => {
    let bondingPairs = 0;
    let lonePairs = recipe.centralLonePairs;
    for (const t of recipe.terminals) {
      bondingPairs += t.bonds;
      lonePairs += t.lonePairs;
    }
    const electrons = (bondingPairs + lonePairs) * 2;
    return { bondingPairs, lonePairs, electrons };
  }, [recipe]);

  // Angles of bonds at the center (for lone-pair placement on the center).
  const centerBondAngles = geom.map((g) => g.angle);

  // Stage gates.
  const showBonds = stage >= 1;
  const showLoneStage = stage >= 2 && showLone;
  const countsRevealed = showCounts || stage >= 3;

  // Smooth opacity per stage element.
  const trans = reduced ? undefined : 'opacity 400ms ease-out';

  // Dim non-highlighted atoms when a spotlight is active.
  const atomOpacity = (lit: boolean) => (anyLit ? (lit ? 1 : 0.32) : 1);

  // Backstop: if a formula was requested that we have no recipe for, show the
  // formula itself rather than silently drawing an unrelated molecule (this used
  // to fall back to H2O). Authors should use chemical_formula / bond_formation
  // for species outside the recipe table; this just prevents a wrong picture.
  const requestedFormula = (config.formula ?? '').replace(/\s+/g, '');
  if (requestedFormula && !lookup(config.formula).supported) {
    return (
      <VizCard
        eyebrow="Bonding · Lewis structures"
        title={config.title ?? 'Lewis electron-dot structure'}
        reducedMotion={reduced}
      >
        <div className="grid w-full place-items-center rounded-md border border-ws-border bg-ws-surface-2 p-8 text-center">
          <div className="font-mono text-3xl font-bold tracking-wide text-ws-ink">{requestedFormula}</div>
          <p className="mt-2 text-xs text-ws-ink-3">
            A Lewis electron-dot diagram isn’t available for this species — showing its formula.
          </p>
        </div>
      </VizCard>
    );
  }

  return (
    <VizCard
      eyebrow="Bonding · Lewis structures"
      title={config.title ?? 'Lewis electron-dot structure'}
      subtitle="Shared pairs = bonds · coloured dots = lone pairs"
      reducedMotion={reduced}
      hint="Pick a molecule · toggle lone pairs & electron count · structure builds itself on load"
    >
      <div className="flex w-full flex-col gap-3 lg:flex-row">
        <div className="relative flex-shrink-0">
          <NowYouTry show={demo.done && !hasCue && !reduced} />
          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="w-full max-w-[360px] rounded-md border border-ws-border bg-ws-surface-2"
            role="img"
            aria-label={`Lewis structure of ${recipe.name}`}
          >
            {/* Bonds (drawn under atoms) */}
            <g style={trans ? { transition: trans } : undefined} opacity={showBonds ? 1 : 0}>
              {geom.map((g, i) => {
                const t = recipe.terminals[i];
                const dx = g.x - CX;
                const dy = g.y - CY;
                const len = Math.hypot(dx, dy) || 1;
                const ux = dx / len;
                const uy = dy / len;
                // Trim the line so it starts/ends at the atom edges.
                const x1 = CX + ux * ATOM_R;
                const y1 = CY + uy * ATOM_R;
                const x2 = g.x - ux * ATOM_R;
                const y2 = g.y - uy * ATOM_R;
                // Perpendicular for multi-bond offset.
                const px = -uy;
                const py = ux;
                const offsets =
                  t.bonds === 1 ? [0] : t.bonds === 2 ? [-3, 3] : [-5, 0, 5];
                const lit = isLit(t.symbol);
                return (
                  <g
                    key={`b${i}`}
                    opacity={atomOpacity(lit)}
                    style={trans ? { transition: 'opacity 300ms ease-out' } : undefined}
                  >
                    {offsets.map((o, k) => (
                      <line
                        key={k}
                        x1={x1 + px * o}
                        y1={y1 + py * o}
                        x2={x2 + px * o}
                        y2={y2 + py * o}
                        stroke={lit ? 'var(--ws-accent)' : 'var(--ws-ink-2)'}
                        strokeWidth={lit ? 3.5 : 2.5}
                        strokeLinecap="round"
                      />
                    ))}
                  </g>
                );
              })}
            </g>

            {/* Lone pairs on the central atom */}
            <g style={trans ? { transition: trans } : undefined} opacity={showLoneStage ? 1 : 0}>
              {lonePairDots(CX, CY, recipe.centralLonePairs, centerBondAngles).map((lp, i) => (
                <g key={`clp${i}`}>
                  <circle cx={lp.x1} cy={lp.y1} r="2.6" fill="var(--ws-accent)" />
                  <circle cx={lp.x2} cy={lp.y2} r="2.6" fill="var(--ws-accent)" />
                </g>
              ))}

              {/* Lone pairs on terminal atoms */}
              {geom.map((g, i) => {
                const t = recipe.terminals[i];
                // Bond from terminal points back toward center.
                const back = Math.atan2(CY - g.y, CX - g.x);
                return lonePairDots(g.x, g.y, t.lonePairs, [back]).map((lp, k) => (
                  <g key={`tlp${i}-${k}`}>
                    <circle cx={lp.x1} cy={lp.y1} r="2.6" fill="var(--ws-accent)" />
                    <circle cx={lp.x2} cy={lp.y2} r="2.6" fill="var(--ws-accent)" />
                  </g>
                ));
              })}
            </g>

            {/* Terminal atoms */}
            {geom.map((g, i) => {
              const t = recipe.terminals[i];
              const fc = chargeText(t.formal);
              const lit = isLit(t.symbol);
              return (
                <g
                  key={`t${i}`}
                  opacity={atomOpacity(lit)}
                  style={trans ? { transition: 'opacity 300ms ease-out' } : undefined}
                >
                  <circle
                    cx={g.x}
                    cy={g.y}
                    r={lit ? ATOM_R + 2 : ATOM_R}
                    fill={elementColor(t.symbol)}
                    stroke={lit ? 'var(--ws-accent)' : 'rgba(0,0,0,0.25)'}
                    strokeWidth={lit ? 2.5 : 1}
                  />
                  <text x={g.x} y={g.y + 4} textAnchor="middle" fontSize="13" fontWeight="700" style={{ fill: DARK_INK }}>
                    {t.symbol}
                  </text>
                  {fc && (
                    <text x={g.x + ATOM_R - 2} y={g.y - ATOM_R + 4} textAnchor="middle" fontSize="11" fontWeight="700" fill="var(--ws-danger)">
                      {fc}
                    </text>
                  )}
                </g>
              );
            })}

            {/* Central atom (on top) */}
            <g
              opacity={atomOpacity(isLit(recipe.central))}
              style={trans ? { transition: 'opacity 300ms ease-out' } : undefined}
            >
              <circle
                cx={CX}
                cy={CY}
                r={isLit(recipe.central) ? ATOM_R + 2 : ATOM_R}
                fill={elementColor(recipe.central)}
                stroke={isLit(recipe.central) ? 'var(--ws-accent)' : 'rgba(0,0,0,0.3)'}
                strokeWidth={isLit(recipe.central) ? 2.5 : 1.5}
              />
              <text x={CX} y={CY + 4} textAnchor="middle" fontSize="14" fontWeight="800" style={{ fill: DARK_INK }}>
                {recipe.central}
              </text>
              {chargeText(recipe.centralFormal) && (
                <text x={CX + ATOM_R - 2} y={CY - ATOM_R + 4} textAnchor="middle" fontSize="11" fontWeight="700" fill="var(--ws-danger)">
                  {chargeText(recipe.centralFormal)}
                </text>
              )}
            </g>
          </svg>
        </div>

        {/* Controls + readout */}
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div>
            <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-ws-ink-3" htmlFor="lewis-molecule">
              Molecule
            </label>
            <select
              id="lewis-molecule"
              value={key}
              onChange={(e) => { setKey(e.target.value); setStage(MAX_STAGE); markInteracted(); }}
              className={`w-full rounded-sm border border-ws-border bg-ws-tile px-2 py-1.5 text-sm font-medium text-ws-ink transition-colors hover:border-ws-border-strong ${VIZ_FOCUS}`}
            >
              {RECIPE_KEYS.map((k) => (
                <option key={k} value={k}>{k} — {RECIPES[k].name}</option>
              ))}
            </select>
          </div>

          {recipe.note && <p className="text-xs text-ws-ink-2">{recipe.note}</p>}

          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => { setShowLone((v) => !v); markInteracted(); }}
              aria-pressed={showLone}
              className={`${VIZ_BTN_GHOST} ${showLone ? '!bg-ws-accent !text-ws-on-accent !border-ws-accent' : ''}`}
            >
              {showLone ? 'Lone pairs: on' : 'Lone pairs: off'}
            </button>
            <button
              type="button"
              onClick={() => { setShowCounts((v) => !v); markInteracted(); }}
              aria-pressed={showCounts}
              className={`${VIZ_BTN_GHOST} ${showCounts ? '!bg-ws-accent !text-ws-on-accent !border-ws-accent' : ''}`}
            >
              {showCounts ? 'Electron count: on' : 'Electron count: off'}
            </button>
          </div>

          {countsRevealed && (
            <dl
              className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-sm border border-ws-border bg-ws-tile p-2 text-xs text-ws-ink-2"
              style={trans ? { transition: trans } : undefined}
            >
              <div><dt className="inline text-ws-ink-3">Bonding pairs: </dt><dd className="inline font-medium">{tally.bondingPairs}</dd></div>
              <div><dt className="inline text-ws-ink-3">Lone pairs: </dt><dd className="inline font-medium">{tally.lonePairs}</dd></div>
              <div className="col-span-2"><dt className="inline text-ws-ink-3">Valence e⁻ shown: </dt><dd className="inline font-medium">{tally.electrons}</dd></div>
            </dl>
          )}

          <p className="text-[11px] leading-snug text-ws-ink-3">
            Lines are shared (bonding) electron pairs; coloured dots are lone pairs.
            Superscript shows formal charge where non-zero.
          </p>
        </div>
      </div>
    </VizCard>
  );
}
